import { inject, Injectable } from "@angular/core";
import { RequestApi } from "../Api/RequestApi";
import { RequestState } from "./RequestState";
import { RequestSearchRequest } from "../Dto/RequestSearchRequest";
import { CreateRequestRequest, UpdateRequestRequest } from "../Dto/CreateRequestRequest";
import { RequestEntity } from "../Dto/RequestModel";
import { createBackendError, getBackendErrorMessage } from "../../../Common/Utility/UtilityError";

@Injectable({ providedIn: "root" })
export class RequestEffects {
  private readonly requestApi = inject(RequestApi);
  private readonly requestState = inject(RequestState);

  async searchRequests(params: RequestSearchRequest = {}): Promise<void> {
    const merged: RequestSearchRequest = { page: 1, limit: 10, ...params };
    this.requestState.setLoading(true);
    this.requestState.setError(null);

    try {
      const response = await this.requestApi.searchRequests(merged);
      this.requestState.setRequests(response.data, response.pagination);
      this.requestState.setSearchParams(merged);
    } catch (exception: unknown) {
      this.requestState.setError(getBackendErrorMessage(exception));
    } finally {
      this.requestState.setLoading(false);
    }
  }

  async createRequest(payload: CreateRequestRequest): Promise<RequestEntity> {
    this.requestState.setLoading(true);
    this.requestState.setError(null);

    try {
      const created = await this.requestApi.createRequest(payload);
      this.requestState.addRequest(created);
      return created;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.requestState.setLoading(false);
    }
  }

  async updateRequest(id: string, payload: UpdateRequestRequest): Promise<RequestEntity> {
    this.requestState.setLoading(true);
    this.requestState.setError(null);

    try {
      const updated = await this.requestApi.updateRequest(id, payload);
      this.requestState.updateRequest(updated);
      return updated;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    } finally {
      this.requestState.setLoading(false);
    }
  }

  async getRequestById(id: string): Promise<RequestEntity | null> {
    try {
      return await this.requestApi.getRequestById(id);
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }

  async confirmRequest(id: string): Promise<RequestEntity> {
    try {
      const updated = await this.requestApi.confirmRequest(id);
      this.requestState.updateRequest(updated);
      return updated;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }

  async fulfillRequest(id: string): Promise<RequestEntity> {
    try {
      const updated = await this.requestApi.fulfillRequest(id);
      this.requestState.updateRequest(updated);
      return updated;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }

  async cancelRequest(id: string): Promise<RequestEntity> {
    try {
      const updated = await this.requestApi.cancelRequest(id);
      this.requestState.updateRequest(updated);
      return updated;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }

  async deleteRequest(id: string): Promise<boolean> {
    try {
      await this.requestApi.deleteRequest(id);
      this.requestState.removeRequest(id);
      return true;
    } catch (exception: unknown) {
      const errorMessage = getBackendErrorMessage(exception);
      this.requestState.setError(errorMessage);
      throw createBackendError(exception);
    }
  }
}
